import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { DoxMascot } from "@/components/Logo";

const MAX_POLLS = 12;
const INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", attempts: 0 });
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", message: "Missing session id" }); return; }

    const poll = async (attempt) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          setStatus({ state: "paid" });
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setStatus({ state: "failed", message: data.status });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setStatus({ state: "timeout" });
          return;
        }
        setStatus({ state: "polling", attempts: attempt + 1 });
        timerRef.current = setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      } catch {
        if (attempt >= MAX_POLLS) { setStatus({ state: "timeout" }); return; }
        timerRef.current = setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      }
    };
    poll(0);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 dash-stage">
      <div className="glass-card p-10 max-w-lg w-full text-center" data-testid="payment-success">
        {status.state === "polling" && (
          <>
            <Loader2 size={40} className="text-dox-orange mx-auto animate-spin" />
            <h1 className="mt-4 font-display font-black text-2xl">Confirming your subscription…</h1>
            <p className="text-white/60 text-sm mt-2">Stripe is finalizing the payment. This usually takes a few seconds.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <div className="animate-float inline-block"><DoxMascot size={72} /></div>
            <div className="mt-3 inline-flex items-center gap-2 text-emerald-400">
              <CheckCircle2 size={18} /> <span className="font-semibold">You&apos;re all set.</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl">Welcome to Dox.</h1>
            <p className="text-white/65 mt-3">Your 14-day trial has started. I&apos;m already on the clock for your business.</p>
            <button
              data-testid="go-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-dox-orange px-6 py-3.5 text-white font-bold hover:bg-dox-orangeHover transition-colors"
            >
              Go to my dashboard <ArrowRight size={16} />
            </button>
          </>
        )}
        {(status.state === "failed" || status.state === "timeout" || status.state === "error") && (
          <>
            <XCircle size={40} className="text-rose-400 mx-auto" />
            <h1 className="mt-4 font-display font-black text-2xl">Something didn&apos;t settle.</h1>
            <p className="text-white/60 text-sm mt-2">
              {status.state === "timeout" ? "Stripe is still processing. Refresh in a minute or check your dashboard." : "Payment didn't complete."}
            </p>
            <Link to="/dashboard" className="mt-6 inline-block rounded-full glass-strong px-6 py-3 text-white text-sm hover:bg-white/10 transition-colors">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 dash-stage">
      <div className="glass-card p-10 max-w-lg w-full text-center" data-testid="payment-cancel">
        <div className="inline-block"><DoxMascot size={64} /></div>
        <h1 className="mt-3 font-display font-black text-2xl">No charge made.</h1>
        <p className="text-white/60 text-sm mt-2">You can start your free trial anytime — Dox will be waiting.</p>
        <Link to="/dashboard" className="mt-6 inline-block rounded-full bg-dox-orange px-6 py-3 text-white font-bold hover:bg-dox-orangeHover transition-colors">Back to dashboard</Link>
      </div>
    </div>
  );
}
